<div class="cr05-vendor">

  <!-- Sticky header bar -->
  <header class="cr05-header">
    <a class="back" (click)="cancel()">&lsaquo; Document Mapping</a>
    <div class="idblock">
      <span class="ids">Vendor Template &mdash; {{ supplierName }}</span>
      <span class="meta">
        Supplier ID {{ supplierId }} &middot; sample: {{ documentName || '—' }} &middot; currency on document: {{ detectedCurrency }}
      </span>
    </div>
    <span class="badge" [class.b-draft]="!isPublished" [class.b-pub]="isPublished">{{ statusLabel }}</span>
    <button type="button" class="btn btn-ghost btn-icon" title="Cancel" aria-label="Cancel" (click)="cancel()">
      <i-feather name="x"></i-feather>
    </button>
    <button type="button" class="btn btn-ghost btn-icon" title="Save Draft" aria-label="Save Draft" (click)="saveDraft()">
      <i-feather name="save"></i-feather>
    </button>
    <button
      type="button"
      class="btn btn-publish btn-icon"
      [disabled]="!canPublish"
      [title]="canPublish ? 'Publish Template' : (totalMandatory - doneMandatory) + ' mandatory mapping(s) remaining'"
      [attr.aria-label]="canPublish ? 'Publish Template' : 'Publish Template (incomplete)'"
      (click)="publish()">
      <i-feather name="check"></i-feather>
    </button>
    <button type="button" class="btn btn-danger btn-icon" title="Delete" aria-label="Delete" (click)="deleteTemplate()">
      <i-feather name="trash-2"></i-feather>
    </button>
  </header>

  <!-- Split workspace -->
  <div class="workspace">

    <!-- LEFT: sample document -->
    <section class="pane pane-doc">
      <div class="doc-bar">
        <span class="title">
          Sample invoice
          <span class="pdf-chip" *ngIf="isPdfDocument()">PDF</span>
          <span class="pdf-chip xls" *ngIf="isSpreadsheetDocument()">EXCEL</span>
        </span>
        <button
          type="button"
          class="tool"
          *ngIf="isSpreadsheetDocument() && sourceFileBlobUrl"
          title="Download source file"
          (click)="downloadSourceFile()">&darr;</button>
      </div>

      <div class="doc-scroll">
        <!-- PDF -->
        <iframe
          *ngIf="isPdfDocument() && (pdfBlobUrl || pdfUrl)"
          [src]="getPdfUrlWithAuth()"
          class="pdf-frame"
          title="Sample document"></iframe>

        <!-- Excel -->
        <ng-container *ngIf="isSpreadsheetDocument()">
          <div class="doc-status" *ngIf="spreadsheetLoading">Loading spreadsheet…</div>
          <div class="doc-status error" *ngIf="!spreadsheetLoading && spreadsheetError">{{ spreadsheetError }}</div>
          <div class="sheet-wrap" *ngIf="!spreadsheetLoading && !spreadsheetError && spreadsheetRows.length">
            <table class="sheet-table">
              <thead>
                <tr>
                  <th class="rownum"></th>
                  <th *ngFor="let col of getSpreadsheetColumnLetters()">{{ col }}</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of spreadsheetRows; let rowIndex = index">
                  <td class="rownum">{{ rowIndex + 1 }}</td>
                  <td *ngFor="let col of getSpreadsheetColumnLetters(); let colIndex = index">
                    {{ getSpreadsheetCellValue(row, colIndex) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="doc-status" *ngIf="!spreadsheetLoading && !spreadsheetError && !spreadsheetRows.length">
            No spreadsheet data to display.
          </div>
        </ng-container>

        <!-- Placeholder -->
        <div class="doc-status" *ngIf="!isPdfDocument() && !isSpreadsheetDocument()">
          <span *ngIf="pdfBlobUrl || sourceFileBlobUrl">Preview not available for this file type.</span>
          <span *ngIf="!pdfBlobUrl && !sourceFileBlobUrl">Loading document…</span>
        </div>
        <div class="doc-status" *ngIf="isPdfDocument() && !pdfBlobUrl && !pdfUrl">Loading PDF…</div>
      </div>
    </section>

    <!-- RIGHT: mapping -->
    <section class="pane pane-map">

      <!-- Completeness panel -->
      <div class="map-head">
        <div class="progress-row">
          <span class="ttl">Template completeness</span>
          <div class="bar"><i [style.width.%]="completenessPct"></i></div>
          <span class="pct">{{ doneMandatory }} / {{ totalMandatory }} mandatory &middot; {{ completenessPct }}%</span>
        </div>
        <div class="chips">
          <span
            *ngFor="let chip of completenessChips"
            class="chipf"
            [class.done]="chip.done"
            [class.todo]="!chip.done"
            (click)="setTab(chip.target)">
            {{ chip.done ? '✓' : '!' }} {{ chip.label }}
          </span>
        </div>
      </div>

      <!-- Tabs -->
      <div class="gtabs">
        <button type="button" class="gtab" [class.active]="activeTab === 'hdr'" (click)="setTab('hdr')">
          Header Fields
          <span class="mini" [class.ok]="headerRemaining === 0">{{ headerRemaining === 0 ? '✓' : headerRemaining }}</span>
        </button>
        <button type="button" class="gtab" [class.active]="activeTab === 'cols'" (click)="setTab('cols')">
          Line-Item Columns
          <span class="mini" [class.ok]="lineRemaining === 0">{{ lineRemaining === 0 ? '✓' : lineRemaining }}</span>
        </button>
      </div>

      <div class="map-scroll">

        <!-- TAB 1: header fields -->
        <div class="tabview" [class.show]="activeTab === 'hdr'">
          <div class="tab-toolbar">
            <p class="hint">
              Map each standard field to a value the system detected on the sample invoice. You can also add custom
              optional mapping rows. Mandatory fields cannot be deleted.
            </p>
            <button type="button" class="btn-add-row" (click)="addHeaderMappingRow()">
              <i-feather name="plus"></i-feather> Add mapping row
            </button>
          </div>

          <div class="map-empty" *ngIf="!detectedHeaders.length">No header values were detected on this document.</div>

          <div
            *ngFor="let field of visibleHeaderFields"
            class="map-row"
            [class.todo]="isHeaderTodo(field)">
            <div class="schema">
              <b>{{ field.label }} <span class="req" *ngIf="field.required">*</span></b>
              <span class="opt">{{ field.required ? 'mandatory' : (field.custom ? 'custom' : 'optional') }}</span>
            </div>
            <div class="arrow">&larr;</div>
            <div class="src" *ngIf="mappingsReady">
              <select
                [name]="'hdr-' + field.key"
                [ngModel]="headerMapping[field.key] || ''"
                (ngModelChange)="onHeaderChange(field.key, $event || null)">
                <option value="">— not mapped —</option>
                <option *ngFor="let d of getAvailableDetected(field.key)" [value]="d.key">
                  {{ d.label }} &nbsp;·&nbsp; {{ formatDetectedOption(d, field.key) }}
                </option>
              </select>
              <span class="preview" *ngIf="getHeaderPreview(field.key)">
                extracted: {{ getHeaderPreview(field.key) }}
              </span>
              <div class="manual-entry" *ngIf="needsManualHeaderEntry(field.key)">
                <label [attr.for]="'manual-' + field.key">Enter manually</label>
                <input
                  [id]="'manual-' + field.key"
                  type="text"
                  [ngModel]="getHeaderManualValue(field.key)"
                  (ngModelChange)="onManualHeaderChange(field.key, $event)"
                  [placeholder]="'Type ' + field.label + ' for this vendor'">
                <span class="manual-hint" *ngIf="!getHeaderManualValue(field.key)">
                  No extracted value — enter a fixed value for this vendor, or map a detected field above.
                </span>
              </div>
            </div>
            <button
              type="button"
              class="row-del"
              *ngIf="canDeleteHeaderRow(field)"
              title="Delete mapping row"
              aria-label="Delete mapping row"
              (click)="deleteHeaderMappingRow(field)">
              <i-feather name="trash-2"></i-feather>
            </button>
          </div>
        </div>

        <!-- TAB 2: line-item columns -->
        <div class="tabview" [class.show]="activeTab === 'cols'">
          <div class="tab-toolbar">
            <p class="hint">
              Tell the system what each detected invoice column means. Use Add column to enter an extracted
              name and value on the left, then map it to a schema label. Delete hides a column from this template.
            </p>
            <button type="button" class="btn-add-row" (click)="addLineMappingRow()">
              <i-feather name="plus"></i-feather> Add column
            </button>
          </div>

          <div class="map-empty" *ngIf="!visibleLineColumns.length">No line-item columns were detected on this document.</div>

          <div
            *ngFor="let col of visibleLineColumns"
            class="col-row"
            [class.ignored]="isColumnIgnored(col.key)">
            <div class="det">
              <b>{{ col.key }}</b>
              <span class="sample">sample: {{ getColumnValue(getPreviewRows()[0], col.key) || '—' }}</span>
            </div>
            <div class="arrow">&rarr;</div>
            <select
              *ngIf="mappingsReady"
              [name]="'col-' + col.key"
              [ngModel]="lineColumnMapping[col.key] || ''"
              (ngModelChange)="onLineChange(col.key, $event)">
              <option
                *ngFor="let opt of lineLabelOptionsByCol[col.key] || []; trackBy: trackLineLabelOption"
                [value]="opt.label === IGNORE_LABEL ? '' : opt.label">
                {{ opt.label }}{{ opt.required ? ' *' : '' }}
              </option>
            </select>
            <button
              type="button"
              class="row-del"
              title="Delete mapping row"
              aria-label="Delete mapping row"
              (click)="deleteLineMappingRow(col)">
              <i-feather name="trash-2"></i-feather>
            </button>
          </div>

          <!-- Live preview -->
          <div class="preview-wrap" *ngIf="getMappedColumns().length && getPreviewRows().length">
            <div class="cap">Live preview — how reviewers will see this vendor's lines</div>
            <div class="pv-scroll">
              <table class="pv">
                <thead>
                  <tr>
                    <th *ngFor="let col of getMappedColumns()">{{ lineColumnMapping[col.key] }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let row of getPreviewRows()">
                    <td *ngFor="let col of getMappedColumns()">{{ getColumnValue(row, col.key) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

      </div>
    </section>
  </div>
</div>
